import React from "react";
import {
  Box,
  Heading,
  Container,
  Text,
  Button,
  Stack,
  Icon,
  useColorModeValue,
  Flex,
} from "@chakra-ui/react";
import { ChevronDown, Download, Mail } from "lucide-react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const Hero: React.FC = () => {
  // Dark mode color values
  const gradientBg = useColorModeValue(
    "linear(to-br, brand.800, brand.600)",
    "linear(to-br, brand.800, brand.600)"
    // "linear(to-br, gray.900, gray.800)"
  );
  const overlayBg = useColorModeValue("blackAlpha.300", "blackAlpha.600");
  const textColor = useColorModeValue("white", "gray.100");
  const subtitleColor = useColorModeValue("brand.100", "brand.200");
  const headingGradient = useColorModeValue(
    "linear(to-r, white, brand.200)",
    "linear(to-r, gray.100, brand.300)"
  );
  const buttonOutlineColor = useColorModeValue("white", "gray.200");
  const buttonOutlineHoverBg = useColorModeValue(
    "whiteAlpha.200",
    "whiteAlpha.300"
  );

  return (
    <Box id="hero">
      <Box
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        bgGradient={gradientBg}
        zIndex={-2}
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        w="full"
        h="full"
        bg={overlayBg}
        zIndex={-1}
      />

      <Container maxW={"7xl"} h="100vh" position="relative">
        <Flex
          align="center"
          justify="center"
          h="full"
          direction="column"
          textAlign="center"
          color={textColor}
        >
          <MotionText
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="medium"
            mb={4}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Hello, I'm
          </MotionText>

          <MotionHeading
            fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
            fontWeight="bold"
            mb={6}
            bgGradient={headingGradient}
            bgClip="text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Jelili Abdullahi A.
          </MotionHeading>

          <MotionText
            fontSize={{ base: "xl", md: "2xl" }}
            color={subtitleColor}
            mb={8}
            maxW="2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            A Dynamic Frontend Developer with 5+ years of experience building
            scalable web and mobile apps using React, Next.js, and React Native
          </MotionText>

          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing={4}
              align="center"
              justify="center"
            >
              <Button
                size="lg"
                leftIcon={<Mail size={20} />}
                variant="solid"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow:
                    "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                }}
                as="a"
                href="#contact"
              >
                Get in Touch
              </Button>
              <Button
                size="lg"
                leftIcon={<Download size={20} />}
                variant="outline"
                borderColor={buttonOutlineColor}
                color={buttonOutlineColor}
                _hover={{
                  bg: buttonOutlineHoverBg,
                  transform: "translateY(-2px)",
                }}
                as="a"
                href="/my-new-resume-2025.pdf"
                download="jelili-abdullahi-resume.pdf"
                target="_blank"
              >
                Download Resume
              </Button>
            </Stack>
          </MotionBox>
        </Flex>

        <MotionBox
          position="absolute"
          bottom={8}
          left="50%"
          transform="translateX(-50%)"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.8,
            delay: 1.2,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        >
          <Icon as={ChevronDown} w={8} h={8} color={textColor} />
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Hero;
